/**
 * Device + layout host — mobile/tablet/desktop mode for apps & shell.
 * Exposed as ctx.device + ctx.layout.
 */
export type ShellMode = "phone" | "tablet" | "desktop";

export type DeviceSnapshot = {
  mode: ShellMode;
  width: number;
  height: number;
  orientation: "portrait" | "landscape";
  pointer: "fine" | "coarse";
  /** CSS px safe insets */
  safe: { top: number; right: number; bottom: number; left: number };
  /** UI scale from OS settings */
  uiScale: number;
};

type Listener = (s: DeviceSnapshot) => void;

function readMode(w: number): ShellMode {
  // Allow user override
  try {
    const o = localStorage.getItem("arclog.shellMode");
    if (o === "phone" || o === "tablet" || o === "desktop") return o;
  } catch {
    /* */
  }
  if (w < 720) return "phone";
  if (w < 1100) return "tablet";
  return "desktop";
}

function snapshot(uiScale = 1): DeviceSnapshot {
  const width = typeof window !== "undefined" ? window.innerWidth : 1280;
  const height = typeof window !== "undefined" ? window.innerHeight : 800;
  const coarse =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  return {
    mode: readMode(width),
    width,
    height,
    orientation: width >= height ? "landscape" : "portrait",
    pointer: coarse ? "coarse" : "fine",
    safe: { top: 0, right: 0, bottom: 0, left: 0 },
    uiScale,
  };
}

class DeviceBus {
  private listeners = new Set<Listener>();
  private last: DeviceSnapshot = snapshot();
  private started = false;

  get(): DeviceSnapshot {
    return this.last;
  }

  /** Force recompute (e.g. after UI scale change) */
  refresh(uiScale?: number) {
    this.last = snapshot(uiScale ?? this.last.uiScale);
    this.applyDom();
    for (const fn of this.listeners) fn(this.last);
  }

  setModeOverride(mode: ShellMode | "auto") {
    try {
      if (mode === "auto") localStorage.removeItem("arclog.shellMode");
      else localStorage.setItem("arclog.shellMode", mode);
    } catch {
      /* */
    }
    this.refresh();
  }

  subscribe(fn: Listener): () => void {
    this.ensure();
    this.listeners.add(fn);
    fn(this.last);
    return () => this.listeners.delete(fn);
  }

  private ensure() {
    if (this.started || typeof window === "undefined") return;
    this.started = true;
    const on = () => this.refresh();
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    try {
      window.matchMedia("(pointer: coarse)").addEventListener("change", on);
    } catch {
      /* */
    }
    this.refresh();
  }

  private applyDom() {
    if (typeof document === "undefined") return;
    const d = this.last;
    document.documentElement.dataset.shellMode = d.mode;
    document.documentElement.dataset.orientation = d.orientation;
    document.documentElement.dataset.pointer = d.pointer;
    document.documentElement.style.setProperty("--os-screen-w", d.width + "px");
    document.documentElement.style.setProperty("--os-screen-h", d.height + "px");
  }
}

export const deviceBus = new DeviceBus();

export type DeviceApi = {
  get(): DeviceSnapshot;
  /** Subscribe to resize/orientation/mode changes */
  subscribe(fn: (s: DeviceSnapshot) => void): () => void;
  /** phone | tablet | desktop | auto */
  setMode(mode: ShellMode | "auto"): void;
  isPhone(): boolean;
  isTablet(): boolean;
  isDesktop(): boolean;
  /** Device presets for Studio preview frames */
  presets(): Array<{ id: string; label: string; width: number; height: number; mode: ShellMode }>;
};

export type LayoutApi = {
  /** Apply flex stack styles for mobile-first columns */
  stack(
    el: HTMLElement,
    opts?: { gap?: number; pad?: number; className?: string }
  ): { el: HTMLElement; destroy(): void };
  /** Switch desktop split → stacked column when mode is phone */
  responsiveSplit(
    host: HTMLElement,
    panes: HTMLElement[],
    opts?: {
      desktopSizes?: number[];
      direction?: "horizontal" | "vertical";
      stackOn?: ShellMode | "phone" | "tablet";
      className?: string;
    }
  ): { destroy(): void; rebuild(): void };
  /** Safe padding helper */
  applySafe(el: HTMLElement): void;
};

export function createDeviceApi(getUiScale: () => number): DeviceApi {
  deviceBus.refresh(getUiScale());
  return {
    get: () => {
      const s = deviceBus.get();
      return { ...s, uiScale: getUiScale() };
    },
    subscribe: (fn) => deviceBus.subscribe(fn),
    setMode: (m) => deviceBus.setModeOverride(m),
    isPhone: () => deviceBus.get().mode === "phone",
    isTablet: () => deviceBus.get().mode === "tablet",
    isDesktop: () => deviceBus.get().mode === "desktop",
    presets: () => [
      { id: "phone-se", label: "Phone SE", width: 375, height: 667, mode: "phone" },
      { id: "phone", label: "Phone", width: 390, height: 844, mode: "phone" },
      { id: "phone-max", label: "Phone Max", width: 430, height: 932, mode: "phone" },
      { id: "tablet", label: "Tablet", width: 768, height: 1024, mode: "tablet" },
      { id: "tablet-ls", label: "Tablet landscape", width: 1024, height: 768, mode: "tablet" },
      { id: "desktop", label: "Desktop", width: 1280, height: 800, mode: "desktop" },
      { id: "desktop-wide", label: "Desktop wide", width: 1440, height: 900, mode: "desktop" },
    ],
  };
}

export function createLayoutApi(device: DeviceApi): LayoutApi {
  return {
    stack(el, opts = {}) {
      el.classList.add("os-layout-stack");
      if (opts.className) el.classList.add(opts.className);
      el.style.display = "flex";
      el.style.flexDirection = "column";
      el.style.gap = (opts.gap ?? 10) + "px";
      if (opts.pad != null) el.style.padding = opts.pad + "px";
      el.style.minHeight = "0";
      el.style.minWidth = "0";
      return {
        el,
        destroy() {
          el.classList.remove("os-layout-stack");
        },
      };
    },
    responsiveSplit(host, panes, opts = {}) {
      let unsub = () => {};
      const stackOn = opts.stackOn || "phone";
      const rebuild = () => {
        host.innerHTML = "";
        host.className = "os-layout-rsplit" + (opts.className ? " " + opts.className : "");
        const mode = device.get().mode;
        const shouldStack = mode === "phone" || (stackOn !== "phone" && mode === "tablet");
        if (shouldStack) {
          host.style.cssText =
            "display:flex;flex-direction:column;width:100%;height:100%;min-height:0;gap:0;overflow:auto";
          for (const p of panes) {
            p.style.flex = "0 0 auto";
            p.style.width = "100%";
            p.style.minHeight = "180px";
            host.appendChild(p);
          }
        } else {
          host.style.cssText = "display:flex;flex-direction:row;width:100%;height:100%;min-height:0";
          const sizes = opts.desktopSizes || panes.map(() => 1);
          const total = sizes.reduce((a, b) => a + b, 0) || 1;
          panes.forEach((p, i) => {
            p.style.flex = `${sizes[i] / total} 1 0`;
            p.style.minWidth = "0";
            p.style.minHeight = "0";
            p.style.height = "100%";
            host.appendChild(p);
            if (i < panes.length - 1) {
              const g = document.createElement("div");
              g.style.cssText = "flex:0 0 5px;cursor:col-resize;background:transparent";
              host.appendChild(g);
            }
          });
        }
      };
      rebuild();
      unsub = device.subscribe(() => rebuild());
      return {
        rebuild,
        destroy() {
          unsub();
          host.innerHTML = "";
        },
      };
    },
    applySafe(el) {
      el.style.paddingTop = "max(12px, env(safe-area-inset-top, 0px))";
      el.style.paddingBottom = "max(12px, env(safe-area-inset-bottom, 0px))";
      el.style.paddingLeft = "max(12px, env(safe-area-inset-left, 0px))";
      el.style.paddingRight = "max(12px, env(safe-area-inset-right, 0px))";
    },
  };
}
